import clsx from 'clsx'
import type { PropsWithChildren, ReactNode, Ref } from 'react'

import { Icon } from '@fv/client-components'
import type { PropsWithClassName } from '@fv/client-types'

import { ActionList } from './ActionList'
import { Pinline } from './Pinline'

type ListProps = PropsWithClassName<
  PropsWithChildren<{
    fcp0?: boolean
    lcp0?: boolean
    bare?: boolean
  }>
>

export const List = ({ children, className, fcp0, lcp0, bare }: ListProps) => {
  return (
    <ul
      className={clsx(
        // Top borders on siblings (v3 divide behavior); v4 divide-y borders the bottom, which ListItem hover/active styles don't expect
        !bare && '[&>*+*]:border-t [&>*+*]:border-fv-gray',
        fcp0 && '[&>li:first-child]:pt-0',
        lcp0 && '[&>li:last-child]:pb-0',
        bare && '[&>li]:py-1',
        className,
      )}
    >
      {children}
    </ul>
  )
}

type ListItemProps = PropsWithClassName<
  PropsWithChildren<{
    actions?: ReactNode
    custom?: boolean
    dashed?: boolean
    dotted?: boolean
    condensed?: boolean
    bulleted?: boolean
    itemsStart?: boolean
    divider?: boolean
    ref?: Ref<HTMLLIElement>
    buttonAction?: boolean | 'active'
    onClick?: () => void
  }>
>
export const ListItem = ({
  children,
  actions,
  className,
  custom,
  dashed,
  dotted,
  condensed,
  bulleted,
  itemsStart,
  divider,
  ref,
  buttonAction,
  onClick,
}: ListItemProps) => {
  return (
    <li
      className={clsx(
        'flex',
        className,
        dashed && 'border-dashed',
        dotted && 'border-dotted',
        !custom && !buttonAction && (condensed ? 'py-1' : 'py-[.6rem]'),
        itemsStart ? 'items-start' : 'items-center',
        // Have to use important on buttonAction border because the <List> sibling border color would otherwise override buttonAction styles
        buttonAction &&
          '[&>a]:py-[.85em] [&>a]:px-4 p-0 -mt-px [&>a:first-child]:block [&>a:first-child]:flex-1 first:border-t-transparent !border-fv-blue/30',

        buttonAction === 'active' &&
          'bg-fv-beer-light [&>a:first-child]:pointer-events-none, [&>a:first-child>.svg--inline-fa]:text-fv-orange-bright',
      )}
      ref={ref}
      onClick={onClick}
    >
      {bulleted && (
        <Icon icon="dot-circle" className="mr-2 shrink-0 text-fv-gray-500" />
      )}
      {children}
      {actions}
      {divider && <Pinline className="my-2 flex-1" />}
    </li>
  )
}

type LiaProps = PropsWithChildren<PropsWithClassName>
export const ListItemActions = ({ children, className }: LiaProps) => {
  return (
    <div className={clsx(className, 'ml-auto')}>
      <ActionList>{children}</ActionList>
    </div>
  )
}

export const ListHeaderActions = ({
  children,
  className,
}: PropsWithClassName<PropsWithChildren>) => {
  return (
    <div
      className={clsx(
        'border-b border-dashed border-[#ccc] pb-2 mb-6 fa-width-auto',
        className,
      )}
    >
      {children}
    </div>
  )
}
